import { z } from 'zod';
/**
 * Turn-based head-to-head mode.
 *
 * Distinct from the racing pool: both players act on ONE shared board, taking
 * turns, until someone wins / it's a draw / someone forfeits. No clock, no
 * segments, no parallel puzzles.
 *
 * The two players occupy SEATS 0 and 1. Seat, not userId, is what the engine
 * reasons about — the orchestrator maps userId↔seat and the seat is never taken
 * from a client payload (same security rule as the racing mode).
 */
export const TURN_GAME_IDS = ['connect-four', 'tic-tac-toe', 'battleship', 'reaction-duel', 'find-match', 'soccer', 'flappy'];
export const TurnGameId = z.enum(TURN_GAME_IDS);
/* ---- Connect Four (standard 7 cols x 6 rows) ---- */
export const CONNECT4_COLS = 7;
export const CONNECT4_ROWS = 6;
export const CONNECT4_WIN = 4;
export const Connect4Move = z.object({ type: z.literal('drop'), col: z.number().int().min(0).max(CONNECT4_COLS - 1) });
/* ---- Tic-Tac-Toe (3x3) ---- */
export const TTT_SIZE = 3;
export const TicTacToeMove = z.object({ type: z.literal('mark'), cell: z.number().int().min(0).max(TTT_SIZE * TTT_SIZE - 1) });
/* ---- Battleship (6x6, ships of length 3,2,2) ---- */
export const BATTLESHIP_SIZE = 6;
export const BATTLESHIP_SHIPS = [3, 2, 2];
/** Place all ships at once (validated server-side). Horizontal or vertical. */
export const BattleshipPlaceMove = z.object({
    type: z.literal('place'),
    ships: z
        .array(z.object({
        row: z.number().int().min(0).max(BATTLESHIP_SIZE - 1),
        col: z.number().int().min(0).max(BATTLESHIP_SIZE - 1),
        length: z.number().int().min(2).max(3),
        horizontal: z.boolean(),
    }))
        .length(BATTLESHIP_SHIPS.length),
});
export const BattleshipFireMove = z.object({
    type: z.literal('fire'),
    cell: z.number().int().min(0).max(BATTLESHIP_SIZE * BATTLESHIP_SIZE - 1),
});
export const BattleshipMove = z.discriminatedUnion('type', [BattleshipPlaceMove, BattleshipFireMove]);
/* ---- Reaction Duel (real-time reflex, ping-fair) ---- */
/** How many rounds decide the duel (best of). */
export const REACTION_ROUNDS = 3;
/** Plausible human reaction bounds (ms). Outside → treated as a fault/false start. */
export const REACTION_MIN_MS = 80;
export const REACTION_MAX_MS = 3_000;
/**
 * A tap. `reactionMs` is measured CLIENT-SIDE — the delta between the client
 * rendering GREEN and the player tapping — so it's inherently ping-immune
 * (network lag cancels out of a local delta). `false` = a false start (tapped
 * before green). The server clamps to plausible bounds and owns the GO instant,
 * so the value can't be gamed into an impossible time.
 */
export const ReactionTapMove = z.object({
    type: z.literal('tap'),
    /** Client-measured ms since green. Null on a false start (tapped early). */
    reactionMs: z.number().min(0).max(60_000).nullable(),
});
/* ---- Find Match (Dobble / Spot-It, simultaneous, ping-fair) ---- */
/** Best of this many rounds. */
export const MATCH_ROUNDS = 3;
/** Symbols per card. Projective plane of order n=7 (prime) ⇒ 8 symbols/card, 57 cards. */
export const MATCH_SYMBOLS_PER_CARD = 8;
/**
 * A tap. `findMs` is measured CLIENT-SIDE (delta from card-shown to tap), so it's
 * ping-immune the same way Reaction Duel's `reactionMs` is. The server owns which
 * symbol is the true match and clamps the timing.
 */
export const MatchTapMove = z.object({
    type: z.literal('tap'),
    symbolId: z.number().int().nonnegative(),
    findMs: z.number().min(0).max(60_000),
});
/* ---- Soccer (Soccer Stars: turn-based flick physics) ---- */
export const SOCCER_FIELD_W = 480;
export const SOCCER_FIELD_H = 320;
export const SOCCER_PUCK_R = 16;
export const SOCCER_BALL_R = 12;
export const SOCCER_PUCKS_PER_SEAT = 5;
/** Goal mouth half-height on each short side. */
export const SOCCER_GOAL_HALF_HEIGHT = 60;
/** Clamp on flick speed (px/s). */
export const SOCCER_MAX_POWER = 900;
export const SOCCER_GOALS_TO_WIN = 1;
/** The move: flick puck `puckIndex` (0..4) of the mover with an aim vector. */
export const SoccerFlickMove = z.object({
    type: z.literal('flick'),
    puckIndex: z.number().int().min(0).max(SOCCER_PUCKS_PER_SEAT - 1),
    /** Aim vector; server normalizes and clamps its magnitude to SOCCER_MAX_POWER. */
    vx: z.number(),
    vy: z.number(),
});
/* ---- Flappy (synced duel: both fly the same course; first to crash loses) ---- */
/** How long the pre-flight 3-2-1 countdown runs before both birds go live. */
export const FLAPPY_COUNTDOWN_MS = 3_000;
/**
 * If both players crash within this window AND on the same pipe, it's a draw
 * (they hit "the same pillar" together). Otherwise the earlier crash loses.
 */
export const FLAPPY_TIE_WINDOW_MS = 250;
/** A flap: local ms since GO + current altitude (for the opponent ghost). */
export const FlappyFlapMove = z.object({
    type: z.literal('flap'),
    tMs: z.number().min(0).max(600_000),
    y: z.number().min(0).max(2_000),
});
/** A crash: local ms since GO, altitude, and which pipe index was hit (-1 = ground). */
export const FlappyCrashMove = z.object({
    type: z.literal('crash'),
    tMs: z.number().min(0).max(600_000),
    y: z.number().min(0).max(2_000),
    pipeIndex: z.number().int().min(-1),
});
export const FlappyDuelMove = z.discriminatedUnion('type', [FlappyFlapMove, FlappyCrashMove]);
//# sourceMappingURL=turn-games.js.map